// @ts-nocheck
import { DeadlineText, contactLine } from "@/components/shared/StackedCell";
import { deadlineStatus } from "@/lib/deadline-status";
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { SegmentedViewSwitcher, type ViewOption } from "@/components/shared/SegmentedViewSwitcher";
import { OutsourceTrackingHubPage } from "./OutsourceTrackingHubPage";
import {
  Plus,
  Trash2,
  Edit,
  FileText,
  MoreHorizontal,
  Truck,
  DollarSign,
  Calendar,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  Package,
  Layers,
  Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { trpcClient } from "@/lib/trpc";
import { formatMoney, formatNumber, cn } from "@/lib/utils";
import { toast } from "sonner";

import { BulkActionBar, BulkAction } from "@/components/shared/BulkActionBar";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { OutsourcePOStatus } from "@/config/status-registry";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import { useConfirm } from "@/providers/ConfirmProvider";
import { type StatusSidebarItem } from "@/components/shared/StatusSidebar";


function getPaymentBadge(paymentStatus: string | null | undefined) {
  const status = (paymentStatus || "UNPAID").toUpperCase();
  switch (status) {
    case "PAID":
      return {
        label: "Đã TT",
        cls: "bg-emerald-50 text-emerald-700 border-emerald-300",
      };
    case "PARTIAL":
      return {
        label: "TT 1 phần",
        cls: "bg-amber-50 text-amber-700 border-amber-300",
      };
    default:
      return {
        label: "Chưa TT",
        cls: "bg-slate-50 text-slate-600 border-slate-200",
      };
  }
}
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import { useRowSelection } from "@/hooks/useRowSelection";
import {
  useDatePeriodFilter,
  DatePeriodFilter,
} from "@/components/shared/DatePeriodFilter";
import { SortableHeader } from "@/components/shared/SortableHeader";
import {
  DateGroupedTable,
  DateGroupToggleButton,
  extractDateKey,
} from "@/components/shared/DateGroupedTable";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { useExcelExport } from "@/hooks/useExcelExport";
import { downloadExcelTemplate, type ExcelColumnDef } from "@/lib/excel.utils";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import {
  InlineFilterRow,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import type { FilterField } from "@/components/shared/FilterBar";

const FILTER_FIELDS: FilterField[] = [
  { key: "poId", label: "Mã ĐGC", type: "text", placeholder: "Mã ĐGC..." },
  { key: "vendorName", label: "Đơn vị gia công", type: "text", placeholder: "Đơn vị gia công..." },
  { key: "expectedDate", label: "Ngày hẹn trả", type: "date", placeholder: "Ngày hẹn trả..." },
];

const STATUS_ITEMS: StatusSidebarItem[] = Object.entries(OutsourcePOStatus).map(
  ([key, cfg]) => ({
    key,
    label: cfg.label,
    color: cfg.color,
    dotColor: cfg.dotColor,
  }),
);

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "poId", label: "Mã ĐGC", width: 14 },
  {
    key: "vendorName",
    label: "Đơn vị gia công",
    width: 25,
    formatter: (_v, po: any) => po.vendor?.name || po.vendor?.shortName || po.vendorName || "",
  },
  {
    key: "vendorCode",
    label: "Mã NCC",
    width: 14,
    formatter: (_v, po: any) => po.vendor?.code || po.vendorCode || "",
  },
  {
    key: "orderId",
    label: "Đơn hàng gốc",
    width: 16,
    formatter: (_v, po: any) => po.order?.orderId || po.orderId || "",
  },
  {
    key: "productionOrderId",
    label: "Mã LSX",
    width: 16,
    formatter: (_v, po: any) => po.productionOrder?.lsxId || po.productionOrderId || "",
  },
  {
    key: "productName",
    label: "Tên sản phẩm",
    width: 25,
    formatter: (_v, po: any) => po.productName || po.order?.orderDetails?.[0]?.productName || "",
  },
  {
    key: "productSpecs",
    label: "Quy cách / Công đoạn",
    width: 22,
    formatter: (_v, po: any) => po.productSpecs || po.process?.name || "",
  },
  { key: "qtyOrdered", label: "SL đặt GC", width: 14, format: "number" },
  { key: "qtyReceived", label: "SL nhận về", width: 14, format: "number" },
  { key: "qtyAccepted", label: "SL đạt", width: 14, format: "number" },
  { key: "qtyRejected", label: "SL lỗi", width: 14, format: "number" },
  { key: "unitPrice", label: "Đơn giá GC", width: 16, format: "currency" },
  { key: "totalAmount", label: "Tổng tiền (VNĐ)", width: 18, format: "currency" },
  {
    key: "issueDate",
    label: "Ngày giao",
    width: 14,
    format: "date",
    formatter: (_v, row: any) => row.issueDate || "",
  },
  {
    key: "expectedDate",
    label: "Ngày hẹn trả",
    width: 14,
    format: "date",
    formatter: (_v, row: any) => row.expectedDate || "",
  },
  {
    key: "actualReceiveDate",
    label: "Ngày thực nhận",
    width: 14,
    format: "date",
    formatter: (_v, row: any) => row.actualReceiveDate || "",
  },
  { key: "status", label: "Trạng thái", width: 16 },
  { key: "paymentStatus", label: "Thanh toán", width: 16 },
];

const OUTSOURCE_VIEW_OPTIONS: ViewOption<"list" | "tracking">[] = [
  { id: "list", label: "Danh Sách Đơn", icon: FileText },
  { id: "tracking", label: "Tiến độ", icon: Truck },
];

export default function OutsourceOrdersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const routerSearch = useRouterState({ select: (s) => s.location.search }) as Record<string, any>;
  const rawView = routerSearch?.view;
  const initialView = rawView === "tracking" ? "tracking" : "list";
  const [activeView, setActiveView] = useState<"list" | "tracking">(initialView);

  useEffect(() => {
    if (rawView) {
      setActiveView(rawView === "tracking" ? "tracking" : "list");
    }
  }, [rawView]);

  const handleViewChange = (v: "list" | "tracking") => {
    setActiveView(v);
    navigate({
      search: (prev: any) => ({ ...prev, view: v }),
      replace: true,
    });
  };

  const confirm = useConfirm();
  const {
    period,
    setPeriod,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    dateParams,
  } = useDatePeriodFilter();

  const {
    items,
    isLoading,
    filterValues,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    statusFilter,
    onStatusChange,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
    sortField,
    sortOrder,
    handleSort,
  } = useServerListFilter<any>({
    queryKey: "outsource-pos",
    queryFn: (params) => trpcClient.outsourcePos.list.query(params) as any,
    extraParams: dateParams.from
      ? { issueDateFrom: dateParams.from, issueDateTo: dateParams.to }
      : {},
  });

  const rowIds = items.map((po: any) => po.id || po.poId);
  const rowSelection = useRowSelection(rowIds);
  const {
    selectedIds,
    selectionMode,
    clearSelection,
    selectedCount,
    toggleSelectAll,
    allSelected,
  } = rowSelection;

  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(new Set());
  // Gom nhóm theo ngày đặt + tổng phụ trên dòng nhóm, như các trang danh sách khác
  const [isDateGrouped, setIsDateGrouped] = useState(true);

  const toggleRowExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const { handleExport, isExporting } = useExcelExport({
    columns: EXPORT_COLUMNS,
    filename: "don_gia_cong_ngoai",
    queryFn: trpcClient.outsourcePos.list.query,
    currentFilters: {
      ...filterValues,
      status: statusFilter || undefined,
      ...(dateParams.from ? { issueDateFrom: dateParams.from, issueDateTo: dateParams.to } : {}),
    },
    selectedIds,
    idKey: "id",
  });

  const getBulkActions = (): BulkAction[] => {
    if (selectedCount === 0) return [];
    return [
      {
        label: "Xuất Excel",
        icon: <Download className="w-3.5 h-3.5" />,
        onClick: () => {
          handleExport();
          clearSelection();
        },
      },
      {
        label: "Xóa",
        icon: <Trash2 className="w-3.5 h-3.5" />,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            actionFn: async (id) =>
              trpcClient.outsourcePos.delete.mutate({ id }),
            actionName: "xóa",
            itemType: "mục",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["outsource-pos"] });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn xóa ${selectedCount} mục đã chọn?`,
          });
        },
        variant: "destructive",
      },
    ];
  };

  const filterTags = React.useMemo(() => {
    return STATUS_ITEMS.map((item) => ({
      ...item,
      count: 0,
    }));
  }, []);

  return (
    <>
      <ListPageShell
        trashModule="outsource-orders"
        tabs={
          <SegmentedViewSwitcher
            options={OUTSOURCE_VIEW_OPTIONS}
            value={activeView}
            onChange={handleViewChange}
            size="sm"
          />
        }
        headerFilters={
          <PageFilterTags
            items={filterTags}
            selectedKeys={
              statusFilter
                ? Array.isArray(statusFilter)
                  ? statusFilter
                  : [statusFilter as string]
                : []
            }
            onSelect={onStatusChange}
          />
        }
        dateFilter={
          <div className="flex items-center gap-2">
            <DateGroupToggleButton
              isGrouped={isDateGrouped}
              onToggle={() => setIsDateGrouped(!isDateGrouped)}
            />
            <DatePeriodFilter
              period={period}
              setPeriod={setPeriod}
              customFrom={customFrom}
              setCustomFrom={setCustomFrom}
              customTo={customTo}
              setCustomTo={setCustomTo}
            />
          </div>
        }
        unitLabel="ĐH GC"
        actions={
          <div className="flex items-center gap-2">
            <ExportImportButtons
              onExport={() => handleExport()}
              exportLoading={isExporting}
              onDownloadTemplate={() => downloadExcelTemplate(EXPORT_COLUMNS, "mau_don_gia_cong")}
            />
            <Button
              size="sm"
              onClick={() => navigate({ to: "/outsource-orders/create" })}
              className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <Plus className="w-3 h-3 mr-1" /> Tạo ĐH GC
            </Button>
          </div>
        }
        filterFields={FILTER_FIELDS}
        filterValues={filterValues}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        showFilter={showFilter}
      >
        {activeView === "tracking" ? (
          <div className="h-full bg-slate-50/50 overflow-y-auto">
            <OutsourceTrackingHubPage />
          </div>
        ) : (
          <table
            className={cn(
              "w-full text-xs caption-bottom apv-compact-table min-w-[1300px]",
              selectionMode && "apv-select-mode",
            )}
          >
          <thead className="apv-table-header">
            <tr>
              <DataTableSelectAllCheckbox
                rowSelection={rowSelection}
                className="w-10 text-center"
              />
              <th className="w-8 px-1 py-1.5 text-center text-slate-400 font-normal">#</th>
              <SortableHeader
                label="Mã Đơn (PO)"
                field="poId"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="apv-col-code w-[130px]"
              />
              <SortableHeader
                label="Đơn vị gia công"
                field="vendor.name"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="min-w-[180px]"
              />
              <th className="min-w-[200px] px-2 py-1.5 text-left">Sản phẩm & Công đoạn</th>
              <th className="w-[140px] px-2 py-1.5 text-left">Tiến độ & SL</th>
              <th className="w-[120px] px-2 py-1.5 text-center">Đơn hàng & LSX</th>
              <SortableHeader
                label="Hạn trả (ETA)"
                field="expectedDate"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="w-[130px]"
              />
              <th className="w-[95px] px-2 py-1.5 text-center">Thanh toán</th>
              <SortableHeader
                label="Giá trị (VNĐ)"
                field="totalAmount"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="text-right w-[110px]"
              />
              <th className="w-[60px] px-2 py-1.5 apv-col-actions"></th>
            </tr>
            {showFilter && (
              <InlineFilterRow
                columns={[
                  CHECKBOX_COL_NULL,
                  null,
                  { key: "poId" },
                  { key: "vendorName" },
                  null,
                  null,
                  null,
                  { key: "expectedDate" },
                  null,
                  null,
                  null,
                ]}
                filterFields={FILTER_FIELDS}
                filterValues={filterValues}
                onFilterChange={onFilterChange}
              />
            )}
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 11 }).map((_, j) => (
                    <td key={j} className="px-2 py-2">
                      <Skeleton className="h-3 w-14" />
                    </td>
                  ))}
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={11}
                  className="text-center text-slate-400 py-12 text-sm"
                >
                  Không có dữ liệu đơn gia công
                </td>
              </tr>
            ) : (
              <DateGroupedTable<any>
                items={items}
                isGrouped={isDateGrouped}
                getDateKey={(po) => extractDateKey(po.issueDate || po.createdAt)}
                renderGroupHeader={(group) => {
                  const groupOrdered = group.items.reduce((acc, po) => acc + (Number(po.qtyOrdered) || 0), 0);
                  const groupReceived = group.items.reduce((acc, po) => acc + (Number(po.qtyReceived) || 0), 0);
                  const groupAmount = group.items.reduce((acc, po) => acc + (Number(po.totalAmount) || 0), 0);
                  return (
                    <tr
                      key={`group-${group.dateKey}`}
                      data-testid="outsource-date-group"
                      className={cn(
                        "border-y font-semibold text-xs transition-colors",
                        group.isToday
                          ? "bg-[#eef2ff] hover:bg-[#e0e7ff] border-indigo-200"
                          : "bg-[#f1f5f9] hover:bg-[#e2e8f0] border-slate-300",
                      )}
                    >
                      <td colSpan={5} className="py-2 px-3 text-slate-800">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 p-0 text-slate-600 hover:text-slate-900 shrink-0"
                            onClick={group.toggleCollapse}
                            title={group.isCollapsed ? "Bung ra" : "Thu gọn"}
                          >
                            {group.isCollapsed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                          </Button>
                          <span className="font-bold text-slate-800 text-[13px] flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                            {group.formattedDate}
                          </span>
                          <Badge variant="outline" className="bg-white/80 text-slate-700 border-slate-300 text-[11px] font-bold px-2 py-0">
                            {group.count} ĐH GC
                          </Badge>
                          {group.isToday && (
                            <Badge className="bg-indigo-600 text-white text-[10px] font-bold px-1.5 py-0 border-0">HÔM NAY</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-left font-mono font-bold whitespace-nowrap" data-testid="outsource-group-qty">
                        <span className="text-[10px] text-slate-600">
                          Đặt: <span className="text-slate-900">{formatNumber(groupOrdered)}</span>
                        </span>
                        <span className="text-[10px] text-emerald-700 ml-2">
                          Nhận: <span className="text-emerald-800">{formatNumber(groupReceived)}</span>
                        </span>
                      </td>
                      <td colSpan={3} className="px-2 py-2" />
                      <td className="px-2 py-2 text-right font-mono font-bold text-slate-900 text-[12px] tabular-nums whitespace-nowrap" data-testid="outsource-group-amount">
                        {formatMoney(groupAmount)}
                      </td>
                      <td className="px-2 py-2 apv-col-actions"></td>
                    </tr>
                  );
                }}
                renderRow={(po: any) => {
                const poKey = po.id || po.poId;
                const percent =
                  po.qtyOrdered > 0
                    ? Math.min(100, Math.round((po.qtyReceived / po.qtyOrdered) * 100))
                    : (String(po.status || "").toUpperCase() === "COMPLETED" ? 100 : 0);
                const isExpanded = expandedRows.has(poKey);
                // Tình trạng hạn trả theo ngày lịch VN (trước đây so trạng thái chữ thường nên đơn đã xong vẫn báo trễ)
                const osStatus = String(po.status || "").toUpperCase();
                const returnedLate = po.actualReceiveDate && po.expectedDate && new Date(po.actualReceiveDate) > new Date(po.expectedDate);
                const etaStatus = deadlineStatus(po.expectedDate, {
                  done: ["COMPLETED", "CLOSED"].includes(osStatus),
                  doneLabel: returnedLate ? "Trả trễ hạn" : "Đã nhận đủ",
                  cancelled: osStatus === "CANCELLED",
                  todayLabel: "Trả hôm nay",
                  overdueLabel: (d) => `Trễ ${d} ngày`,
                });
                // Thanh toán theo phiếu chi NCC gắn chứng từ (04/11/2026)
                const payBadge = getPaymentBadge(po.paymentInfo?.status);
                const linkedOrder = po.order?.orderId || po.orderId;
                const linkedLsx = po.productionOrder?.orderId || po.productionOrderId;
                const customerName = po.order?.customer?.shortName || po.order?.customer?.name || po.customer?.name;

                return (
                  <React.Fragment key={poKey}>
                    <DataTableRow
                      id={poKey}
                      rowSelection={rowSelection}
                      className={cn(
                        "cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0",
                        selectedIds.has(poKey) && "bg-indigo-50/60",
                        isExpanded && "bg-slate-50/50",
                      )}
                      onClick={() =>
                        navigate({
                          to: "/outsource-orders/$orderId",
                          params: { orderId: poKey },
                        })
                      }
                    >
                      <DataTableRowCheckbox
                        id={poKey}
                        rowSelection={rowSelection}
                      />

                      {/* Expand Chevron */}
                      <td
                        className="w-8 px-1 py-1.5 text-center"
                        onClick={(e) => toggleRowExpand(poKey, e)}
                      >
                        <button
                          type="button"
                          className="p-1 rounded hover:bg-slate-200 text-slate-500 transition-colors"
                          title={isExpanded ? "Thu gọn chi tiết" : "Xem chi tiết công đoạn & tiến độ"}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-3.5 w-3.5 text-indigo-600" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                          )}
                        </button>
                      </td>

                      {/* Mã PO & Trạng thái */}
                      <td className="apv-col-code px-2 py-1.5">
                        <div className="flex flex-col items-start gap-1">
                          <span className="font-mono text-xs font-bold text-indigo-600 leading-tight">
                            {po.poId}
                          </span>
                          <div className="flex items-center gap-1.5">
                            {po.poReference && (
                              <span className="text-[10px] text-slate-400 font-bold uppercase truncate max-w-[80px]">
                                {po.poReference}
                              </span>
                            )}
                            <StatusBadge
                              type="OutsourcePOStatus"
                              value={po.status}
                              className="text-[9px] px-1 py-0 min-h-0 h-4 border-none"
                            />
                          </div>
                        </div>
                      </td>

                      {/* NCC Gia công */}
                      <td className="px-2 py-1.5 truncate max-w-[190px]">
                        <div className="font-semibold text-slate-900 leading-tight truncate">
                          {po.vendor?.name || po.vendorName || "—"}
                        </div>
                        {(contactLine(po.vendor?.contactPerson, po.vendor?.phone) || po.vendor?.code) && (
                          <div className="text-[11px] text-slate-500 mt-0.5 truncate" data-testid="outsource-row-contact">
                            {contactLine(po.vendor?.contactPerson, po.vendor?.phone) || po.vendor?.code}
                          </div>
                        )}
                      </td>

                      {/* Sản phẩm & Quy cách / Công đoạn */}
                      <td className="px-2 py-1.5 truncate max-w-[210px]">
                        <div className="font-medium text-slate-800 truncate text-[11px]" title={po.product?.name || po.productName || ""}>
                          {po.product?.name || po.productName || "Gia công công đoạn"}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate flex items-center gap-1 mt-0.5" title={po.productSpecs || po.process?.name || ""}>
                          <Layers className="h-2.5 w-2.5 text-indigo-500 shrink-0" />
                          <span className="font-medium text-indigo-600">{po.process?.name || po.productSpecs || "Quy cách chung"}</span>
                        </div>
                      </td>

                      {/* Tiến độ & Số lượng */}
                      <td className="px-2 py-1.5">
                        <div className="space-y-1">
                          <div className="flex justify-between text-[10px] font-bold text-slate-600 tabular-nums">
                            <span>
                              {formatNumber(po.qtyReceived)} / {formatNumber(po.qtyOrdered)}
                            </span>
                            <span className={cn(
                              percent >= 100 ? "text-emerald-600" : percent > 0 ? "text-indigo-600" : "text-slate-400"
                            )}>{percent}%</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={cn(
                                "h-full transition-all duration-300",
                                percent >= 100
                                  ? "bg-emerald-500"
                                  : percent > 0
                                  ? "bg-indigo-500"
                                  : "bg-slate-200",
                              )}
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                        </div>
                      </td>

                      {/* Đơn hàng gốc & LSX liên kết */}
                      <td className="px-2 py-1.5 text-center">
                        <div className="flex flex-col items-center gap-1">
                          {linkedOrder ? (
                            <Badge className="text-[9px] px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200 font-mono">
                              {linkedOrder}
                            </Badge>
                          ) : (
                            <span className="text-slate-300 text-[10px]">—</span>
                          )}
                          {linkedLsx && (
                            <span className="text-[9px] text-slate-500 font-mono">
                              LSX: {linkedLsx}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Hạn trả (ETA) & Cảnh báo trễ */}
                      <td className="px-2 py-1.5">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1 text-[11px] text-slate-700 font-mono">
                            <Calendar className="h-3 w-3 text-slate-400 shrink-0" />
                            <span>
                              {po.expectedDate
                                ? new Date(po.expectedDate).toLocaleDateString("vi-VN")
                                : "—"}
                            </span>
                          </div>
                          <span className="text-[11px]"><DeadlineText status={etaStatus} testId="outsource-row-eta-status" /></span>
                        </div>
                      </td>

                      {/* Trạng thái thanh toán */}
                      <td className="px-2 py-1.5 text-center">
                        {po.paymentInfo && po.paymentInfo.status !== "NONE" ? (
                          <span
                            className="inline-flex flex-col items-center gap-0.5"
                            title={`Phải trả ${formatMoney(po.paymentInfo.payable)} đ · đã chi ${formatMoney(po.paymentInfo.paid)} đ`}
                            data-testid="doc-payment-cell"
                          >
                            <Badge className={cn("text-[9px] px-1.5 py-0 h-4 border shadow-2xs font-medium", payBadge.cls)}>
                              {payBadge.label}
                            </Badge>
                            {po.paymentInfo.paid > 0 && <span className="text-[10px] text-slate-500 tabular-nums">{formatMoney(po.paymentInfo.paid)}</span>}
                          </span>
                        ) : (
                          <span className="text-slate-400" title="Chưa phát sinh công nợ nhà cung cấp cho chứng từ này">—</span>
                        )}
                      </td>

                      {/* Giá trị */}
                      <td className="px-2 py-1.5 text-right font-mono font-bold text-slate-900 tabular-nums">
                        {formatMoney(po.totalAmount)}
                      </td>

                      {/* Thao tác */}
                      <td
                        className="px-2 py-1.5 text-right apv-col-actions"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuLabel className="text-xs">
                              Thao tác
                            </DropdownMenuLabel>
                            {/* Nhận hàng gia công làm ở trang chi tiết PO gia công */}
                            <DropdownMenuItem
                              onClick={() => navigate({ to: "/outsource-orders/$orderId", params: { orderId: poKey } })}
                            >
                              <Truck className="mr-2 h-4 w-4" /> Cập nhật nhận hàng
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                navigate({
                                  to: "/outsource-orders/$orderId/edit",
                                  params: { orderId: poKey },
                                })
                              }
                            >
                              <Edit className="mr-2 h-4 w-4" /> Chỉnh sửa
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </DataTableRow>

                    {/* Expandable Child Row: Chi tiết quy cách gia công */}
                    {isExpanded && (
                      <tr className="bg-slate-50/80 border-b border-slate-200">
                        <td colSpan={11} className="p-2.5 pl-10">
                          <div className="bg-white rounded-md border border-slate-200 shadow-2xs p-3">
                            <div className="flex items-center justify-between pb-2 border-b border-slate-100 text-[11px]">
                              <span className="font-semibold text-slate-800 flex items-center gap-1.5">
                                <Package className="h-3.5 w-3.5 text-indigo-600" />
                                Chi tiết gia công ngoài ({po.poId}) - {po.vendor?.name || po.vendorName}
                              </span>
                              {customerName && (
                                <span className="text-slate-500 font-medium">
                                  Khách hàng liên kết: <strong className="text-slate-800">{customerName}</strong>
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 text-[11px]">
                              <div>
                                <span className="text-slate-400 block text-[10px]">Công đoạn / Quy cách:</span>
                                <span className="font-semibold text-slate-800">{po.process?.name || po.productSpecs || "Theo tiêu chuẩn PO"}</span>
                              </div>
                              <div>
                                <span className="text-slate-400 block text-[10px]">Mô hình triển khai:</span>
                                <span className="font-medium text-slate-700">{po.deploymentModel || "Gia công linh hoạt"}</span>
                              </div>
                              <div>
                                <span className="text-slate-400 block text-[10px]">Tỷ lệ hao hụt cho phép:</span>
                                <span className="font-medium text-slate-700">{Number(po.allowanceRate || 0.01) * 100}%</span>
                              </div>
                              <div>
                                <span className="text-slate-400 block text-[10px]">Đơn giá gia công:</span>
                                <span className="font-bold text-slate-900">{formatMoney(po.unitPrice)} / đơn vị</span>
                              </div>
                            </div>
                            <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] bg-slate-50/60 p-2 rounded">
                              <div className="flex gap-4">
                                <span>SL Đặt: <strong className="text-slate-800 tabular-nums">{formatNumber(po.qtyOrdered)}</strong></span>
                                <span>SL Đã nhận: <strong className="text-indigo-600 tabular-nums">{formatNumber(po.qtyReceived)}</strong></span>
                                <span>SL Đạt: <strong className="text-emerald-600 tabular-nums">{formatNumber(po.qtyAccepted)}</strong></span>
                                <span>SL Lỗi: <strong className={cn("tabular-nums", po.qtyRejected > 0 ? "text-rose-600 font-bold" : "text-slate-400")}>{formatNumber(po.qtyRejected)}</strong></span>
                              </div>
                              <div>
                                <span>Tổng thành tiền: <strong className="text-slate-900 font-bold">{formatMoney(po.totalAmount)}</strong></span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
                }}
              />
            )}
          </tbody>
        </table>
        )}
      </ListPageShell>

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />
    </>
  );
}
